import React, { useEffect, useState } from 'react'

import { Accordion, AccordionItem } from '@react/react-spectrum/Accordion'
import Button from '@react/react-spectrum/Button'
import Checkbox from '@react/react-spectrum/Checkbox'
import FieldLabel from '@react/react-spectrum/FieldLabel'
import Info from '@react/react-spectrum/Icon/Info'
import Radio from '@react/react-spectrum/Radio'
import RadioGroup from '@react/react-spectrum/RadioGroup'

import Multifield from 'components/Multifield'
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger'
import Tooltip from '@react/react-spectrum/Tooltip'

import Editor from './Editor'
import { FormTable } from 'components/FormTable'
import { ArchitectureEditor } from './ArchitectureEditor'
import { SimulatorEditor } from './SimulatorEditor'

import { useProject } from 'hooks'

import './ProfileViewer.scss'

const DOCS_DYNAMIC_TITLES_LINK =
  'https://docs.adobedemo.com/projects/project-types/web/faq#how-to-use-value-from-input-aep-in-texts'

function ProfileViewer() {
  const { project, canEdit, type, updateProjectPart } = useProject()
  const [profileViewer, setProfileViewer] = useState(project?.profileViewer || {})
  const [shouldSave, setShouldSave] = useState(false)

  const isWeb = type === 'web'
  const isWebModular = type === 'web-modular'
  const isWebSocial = type === 'web-social'
  const isExternalWebsite = type === 'external-website' || type === 'web-modular'
  const isJourneyTrigger = type === 'journey-trigger'
  const isCustomerExperienceAppV2 = type === 'customer-experience-v2'

  const onPropertySave = (propertyName) => (value) => {
    setShouldSave(true)
    setProfileViewer((pv) => ({ ...pv, [propertyName]: value }))
  }

  const onSectionSave = (section) => (content) => {
    setShouldSave(true)
    setProfileViewer((pv) => ({ ...pv, [section]: content }))
  }

  useEffect(() => {
    if (profileViewer && shouldSave) {
      updateProjectPart('profileViewer', profileViewer)
    }
  }, [profileViewer, shouldSave])

  return (
    <div className='profile-viewer-wrapper'>
      {(isWebModular || isWebSocial) && (
        <div className='profile-viewer-disable'>
          <Checkbox
            label='Disable Profile Viewer'
            defaultChecked={profileViewer.disabled}
            onChange={onPropertySave('disabled')}
            disabled={!canEdit}
          />
          <br />
        </div>
      )}

      {(isWeb || isWebModular || isWebSocial || isExternalWebsite) && (
        <div className='profile-viewer-layout'>
          <FieldLabel label='Layout'>
            <RadioGroup defaultSelectedValue={profileViewer.position || 'left'} onChange={onPropertySave('position')}>
              <Radio label='Side panel' value='left' disabled={!canEdit} />
              <Radio label='Bottom dashboard' value='bottom' disabled={!canEdit} />
            </RadioGroup>
          </FieldLabel>
        </div>
      )}

      {(isWeb || isWebModular || isWebSocial || isExternalWebsite) && (
        <div className='profile-viewer-theme'>
          <FieldLabel label='Theme'>
            <RadioGroup defaultSelectedValue={profileViewer.theme || 'light'} onChange={onPropertySave('theme')}>
              <Radio label='Light' value='light' />
              <Radio label='Dark' value='dark' />
            </RadioGroup>
          </FieldLabel>
        </div>
      )}
      <div className='profile-viewer-editor'>
        <Accordion multiselectable defaultSelectedIndex={isJourneyTrigger ? 1 : 0}>
          {!isJourneyTrigger && !isCustomerExperienceAppV2 && (
            <AccordionItem header='Identities'>
              <Editor
                selectorName='path'
                selectorLabel='Path'
                properties={profileViewer.identities}
                onSave={onSectionSave('identities')}
                disabled={!canEdit}
              />
            </AccordionItem>
          )}

          <AccordionItem header='Profile attributes'>
            {isWebModular && (
              <Checkbox
                label='Enable profile polling'
                defaultChecked={profileViewer.profilePollingEnabled}
                onChange={onPropertySave('profilePollingEnabled')}
                disabled={!canEdit}
              />
            )}
            <Editor
              selectorName='path'
              selectorLabel='Path'
              properties={profileViewer.profileFields}
              onSave={onSectionSave('profileFields')}
              disabled={!canEdit}
            />
          </AccordionItem>

          {!isJourneyTrigger && !isCustomerExperienceAppV2 && (
            <AccordionItem header='Events'>
              <FormTable
                projectType={type}
                fields={profileViewer.eventGroups}
                columns={[
                  {
                    name: 'label',
                    label: 'Event Type Label',
                    tooltip:
                      'https://demo-system-next.s3.eu-north-1.amazonaws.com/argon/tooltips/profile-viewer-type.png',
                  },
                  { name: 'filter', label: 'Event Type Filter' },
                  {
                    name: 'title',
                    label: 'Title',
                    tooltip:
                      'https://demo-system-next.s3.eu-north-1.amazonaws.com/argon/tooltips/profile-viewer-title.png',
                    tooltipFooter: (
                      <span>
                        The events titles can refer to the event data from AEP when using proper selectors. See{' '}
                        <a href={DOCS_DYNAMIC_TITLES_LINK} target='_blank' rel='noreferrer noopener'>
                          the documentation
                        </a>{' '}
                        for more information.
                      </span>
                    ),
                  },
                  {
                    name: 'image',
                    label: 'image',
                    tooltip:
                      'https://demo-system-next.s3.eu-north-1.amazonaws.com/argon/tooltips/profile-viewer-image.png',
                  },
                ]}
                onChange={onSectionSave('eventGroups')}
                disabled={!canEdit}
              />
            </AccordionItem>
          )}

          {(isWeb || isExternalWebsite) && (
            <AccordionItem header='Offers'>
              <Checkbox
                label='Show offers section'
                defaultChecked={profileViewer.offers?.showSection !== false}
                onChange={(value) => onSectionSave('offers')({ ...profileViewer.offers, showSection: value })}
                disabled={!canEdit}
              />

              <FieldLabel label='Decisions/Activity Filters' className='activity-filter'>
                <OverlayTrigger placement='right'>
                  <Button variant='tool' icon={<Info />} />
                  <Tooltip variant='info'>
                    Add keywords to filter offers in profile viewer based on the decision/activity name
                  </Tooltip>
                </OverlayTrigger>
                <Multifield
                  type='text'
                  value={profileViewer.offers?.activityFilter}
                  onChange={(value) => onSectionSave('offers')({ ...profileViewer.offers, activityFilter: value })}
                  disabled={!canEdit}
                />
              </FieldLabel>
            </AccordionItem>
          )}

          {(isWeb || isExternalWebsite) && (
            <AccordionItem header='Loyalty Challenges'>
              <Checkbox
                label='Show Loyalty Challenges section'
                defaultChecked={profileViewer.loyaltyChallenges?.showSection}
                onChange={(value) =>
                  onSectionSave('loyaltyChallenges')({ ...profileViewer.loyaltyChallenges, showSection: value })
                }
                disabled={!canEdit}
              />
            </AccordionItem>
          )}

          {!isJourneyTrigger && (
            <AccordionItem header='Audiences'>
              <Checkbox
                label='Hide exited audiences'
                defaultChecked={profileViewer.segments?.hideExited}
                onChange={(value) => onSectionSave('segments')({ ...profileViewer.segments, hideExited: value })}
                disabled={!canEdit}
              />
              <FieldLabel label='Audience Filters' className='segment-filter'>
                <OverlayTrigger placement='right'>
                  <Button variant='tool' icon={<Info />} />
                  <Tooltip variant='info'>
                    Add keywords to filter audiences in profile viewer based on the audience name
                  </Tooltip>
                </OverlayTrigger>
                <Multifield
                  type='text'
                  value={profileViewer.segments?.segmentFilter}
                  onChange={(value) => onSectionSave('segments')({ ...profileViewer.segments, segmentFilter: value })}
                  disabled={!canEdit}
                />
              </FieldLabel>
            </AccordionItem>
          )}

          {isExternalWebsite && (
            <AccordionItem header='Architecture'>
              <Checkbox
                label='Show architecture section'
                defaultChecked={profileViewer.architecture?.showSection}
                onChange={(value) =>
                  onSectionSave('architecture')({ ...profileViewer.architecture, showSection: value })
                }
                disabled={!canEdit}
              />

              <ArchitectureEditor
                fields={profileViewer.architecture?.diagrams || []}
                onChange={(diagrams) => onSectionSave('architecture')({ ...profileViewer.architecture, diagrams })}
                disabled={!canEdit}
              />
            </AccordionItem>
          )}

          {isExternalWebsite && (
            <AccordionItem header='Debug'>
              <Checkbox
                label='Show debug section'
                defaultChecked={profileViewer.debug?.showSection}
                onChange={(value) => onSectionSave('debug')({ ...profileViewer.debug, showSection: value })}
                disabled={!canEdit}
              />
            </AccordionItem>
          )}

          {(isWeb || isExternalWebsite) && (
            <AccordionItem header='Decisioning'>
              <Checkbox
                label='Show decisioning renderer section'
                defaultChecked={profileViewer.surfaces?.showSection}
                onChange={(value) => onSectionSave('surfaces')({ ...profileViewer.surfaces, showSection: value })}
                disabled={!canEdit}
              />
              <Checkbox
                label='Automatically apply decisioning'
                defaultChecked={profileViewer.surfaces?.autoApply === true}
                onChange={(value) => onSectionSave('surfaces')({ ...profileViewer.surfaces, autoApply: value })}
                disabled={!canEdit}
              />

              {type === 'external-website' && (
                <p className='ProfileViewer__hint'>
                  Configure surfaces in the project's <strong>Decisioning Surfaces</strong> tab.
                </p>
              )}
            </AccordionItem>
          )}

          {(isWeb || isWebModular || isExternalWebsite) && (
            <AccordionItem header='Profile Updater'>
              <Checkbox
                label='Show Profile Updater section'
                defaultChecked={profileViewer.simulator?.showSection}
                onChange={(value) => onSectionSave('simulator')({ ...profileViewer.simulator, showSection: value })}
                disabled={!canEdit}
              />

              <SimulatorEditor
                fields={profileViewer.simulator?.fields || []}
                onChange={(fields) => onSectionSave('simulator')({ ...profileViewer.simulator, fields })}
                disabled={!canEdit}
              />
            </AccordionItem>
          )}
        </Accordion>
      </div>
    </div>
  )
}

export default ProfileViewer
